import { useEffect, useState } from 'react';
import { docFromPlainText, RichEditor } from '@odo/shared-ui';
import { SectionToggle } from '../patient-files/actions/SectionToggle';
import {
    EMPTY_TIPTAP_DOC,
    FIELD_LABEL_CLASS,
    INPUT_CLASS,
    SUBSECTION_LABEL_CLASS,
    type SectionProps
} from './eventFormShared';

// Half the height of the banner-detail body editor (`BODY_EDITOR_MIN_HEIGHT_PX`
// in `ContentSection.tsx`) - this is an intro line, not a body.
const POPUP_SUBTITLE_EDITOR_MIN_HEIGHT_PX = 2 * 24 + 2 * 10;

const SUBTITLE_TOGGLE_ID = 'event-modal-show-subtitle';

/** The sign-up modal / announcement pop-up's own title and subtitle, prepopulated
 *  from the banner's on load/mirroring so the author always edits real text -
 *  see `resolveEventSurface` in `@odo/shared-ui` for how a blank override (sent
 *  as `null`) still inherits the banner on every read surface. */
export function EventPopupSection({ draft, set, isAnnouncement, seeded, canManage }: SectionProps) {
    // A seeded draft never mirrors: editing an existing event, and duplicating
    // one, both arrive with these fields already filled from the source event's
    // own overrides. Mirroring would overwrite them on mount.
    const [titleTouched, setTitleTouched] = useState(seeded);
    const [subtitleTouched, setSubtitleTouched] = useState(seeded);

    useEffect(() => {
        if (titleTouched) return;
        set('modalTitle', draft.title);
    }, [draft.title, titleTouched, set]);

    useEffect(() => {
        if (subtitleTouched) return;
        set('modalSubtitleDoc', docFromPlainText(draft.subtitle));
    }, [draft.subtitle, subtitleTouched, set]);

    const toggleShowSubtitle = () => set('modalShowSubtitle', !draft.modalShowSubtitle);

    return (
        <div className="flex flex-col gap-3">
            <label className={SUBSECTION_LABEL_CLASS}>Pop-up</label>
            <p className="text-[11px] text-gray-400 -mt-1">
                What the {isAnnouncement ? 'announcement pop-up' : 'sign-up modal'} shows, starting
                from the banner's title and subtitle - edit either freely.
            </p>
            <div className="grid grid-cols-1 gap-4">
                <div>
                    <label htmlFor="event-modal-title" className={FIELD_LABEL_CLASS}>
                        Pop-up title
                    </label>
                    <input
                        id="event-modal-title"
                        type="text"
                        value={draft.modalTitle}
                        onChange={(e) => {
                            setTitleTouched(true);
                            set('modalTitle', e.target.value);
                        }}
                        placeholder="Event title"
                        className={INPUT_CLASS}
                    />
                </div>
                <div>
                    <div
                        className={`flex items-center justify-between gap-2 ${SUBSECTION_LABEL_CLASS}`}
                    >
                        <label htmlFor={SUBTITLE_TOGGLE_ID} className="cursor-pointer select-none">
                            Pop-up subtitle
                        </label>
                        <SectionToggle
                            id={SUBTITLE_TOGGLE_ID}
                            enabled={draft.modalShowSubtitle}
                            onToggle={toggleShowSubtitle}
                        />
                    </div>
                    <div
                        className={`mt-1.5 border border-gray-300 rounded-md overflow-hidden ${
                            draft.modalShowSubtitle
                                ? ''
                                : 'opacity-40 pointer-events-none select-none'
                        }`}
                    >
                        <RichEditor
                            value={draft.modalSubtitleDoc ?? EMPTY_TIPTAP_DOC}
                            onChange={(doc) => {
                                setSubtitleTouched(true);
                                set('modalSubtitleDoc', doc);
                            }}
                            allowImages={false}
                            allowTextStyling
                            chrome="flush"
                            minHeightPx={POPUP_SUBTITLE_EDITOR_MIN_HEIGHT_PX}
                            readOnly={!canManage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EventPopupSection;
